import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import SearchInput from "../../../components/form/SearchInput";
import FilterByButton from "../../ui/FilterButton";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import { clearOrders } from "../../../redux/features/orderSlice";
import Button from "../../ui/Button";
import DateRangeSelector, { DateRangeType } from "../../ui/DateRangeSelector";
import RequestSummary from "./RequestSummary";
import RequestsTable from "./RequestsTable";
import { statusFilterOptions } from "./util";
import { mapDataToLabelValue } from "../../../helpers";
import ShoppingBagsTable from "./BagsTable";
import PaidBagsTable from "./PaidBagsTable";

const OrderManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = Number(searchParams.get("tab")) || 1; // Default to 1 if not in URL

  const [currentTable, setCurrentTable] = useState<number>(initialTab);
  const [search, setSearch] = useState<string>("");
  const [inputValue, setInputValue] = useState("");
  const [filter, setFilter] = useState<string>("");
  const [filterByCategory, setFilterByCategory] = useState<string>("");
  const [isActive, setIsActive] = useState(null);

  const { buyersRequests, requestSummary, activeBags, paidBags } =
    useAppSelector((state: RootState) => state.requests);
  const { user }: any = useAppSelector((state) => state.auth);

  const [selectedRange, setSelectedRange] = useState<DateRangeType>({
    startDate:
      user?.user?.roles === "INVESTOR"
        ? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split("T")[0]
        : null,
    endDate: null,
  });

  const handleDateChange = (range: DateRangeType) => {
    setSelectedRange(range);
  };

  const dispatch = useAppDispatch();

  const tabList = [
    { id: 1, name: "All Requests", count: buyersRequests?.meta?.total ?? 0 },
    { id: 2, name: "All Active Bags", count: activeBags?.meta?.total ?? 0 },
    { id: 3, name: "Paid Bags", count: paidBags?.meta?.total ?? 0 },
  ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearch(inputValue);
    }
  };

  useEffect(() => {
    setSearchParams({ tab: currentTable.toString() });
  }, [currentTable, setSearchParams]);

  return (
    <div className="flex flex-col gap-4 sm:gap-4 p-4 sm:p-6 md:p-8 bg-gray-100 min-h-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold mb-2">Request Management</h1>
      </div>

      <RequestSummary />

      <WhiteTitledCard>
        <div className="px-6">
          <div className="flex md:items-center justify-between flex-col lg:flex-row mb-4 gap-6">
            <DateRangeSelector
              initialRange={selectedRange}
              onChange={handleDateChange}
            />
            <div className="flex-col md:flex-row md:items-center gap-4 flex">
              {currentTable == 1 && (
                <div className="flex gap-2">
                  <FilterByButton
                    options={statusFilterOptions}
                    onSelect={(value) => {
                      setFilter(value);
                      dispatch(clearOrders());
                    }}
                    buttonLabel="Request status"
                  />
                  <FilterByButton
                    options={mapDataToLabelValue(
                      requestSummary?.topCategories,
                      "category",
                      "category"
                    )}
                    onSelect={(value) => {
                      setFilterByCategory(value);
                      dispatch(clearOrders());
                    }}
                    buttonLabel="Category"
                  />
                </div>
              )}

              {currentTable === 1 && (
                <>
                  <SearchInput
                    placeholder="Search"
                    onChange={(e: any) => setInputValue(e.target.value)}
                    value={inputValue}
                    onKeyDown={handleKeyDown}
                  />
                  <Button
                    customStyle={{
                      width: "auto",
                      height: "40px",
                    }}
                    label="filter"
                    onClick={() => setSearch(inputValue)}
                    classNames="hidden sm:flex"
                  />
                </>
              )}

              {(currentTable === 2 || currentTable === 3) && (
                <FilterByButton
                  options={[
                    { label: "All", value: null },
                    { label: "Active", value: true },
                    { label: "InActive", value: false },
                  ]}
                  onSelect={(value) => {
                    setIsActive(value);
                  }}
                  buttonLabel="Bag Status"
                  defaultValue={isActive}
                />
              )}
            </div>
          </div>

          {/* 🧭 Tabs */}
          <div className="flex space-x-4 items-center">
            {tabList?.map((tab) => (
              <div
                key={tab.id}
                className={`space-x-2 cursor-pointer text-sm font-semibold p-2 text-black ${
                  currentTable === tab.id && "border-b-2 border-primary"
                }`}
                onClick={() => {
                  setCurrentTable(tab.id);
                  setSearch("");
                  setFilter("");
                  setIsActive(null);
                  setSelectedRange({
                    startDate: null,
                    endDate: null,
                  });
                }}
              >
                <span>{tab.name}</span>
                <span className="bg-lightPurple px-2 text-accent rounded-md">
                  {tab?.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 📋 Tables */}
        {currentTable === 1 && (
          <RequestsTable
            status={filter}
            search={search}
            filterByCategory={filterByCategory}
            startDate={selectedRange?.startDate}
            endDate={selectedRange?.endDate}
          />
        )}
        {currentTable === 2 && (
          <ShoppingBagsTable
            isActive={isActive}
            startDate={selectedRange?.startDate}
            endDate={selectedRange?.endDate}
          />
        )}
        {currentTable === 3 && (
          <PaidBagsTable
            isActive={isActive}
            startDate={selectedRange?.startDate}
            endDate={selectedRange?.endDate}
          />
        )}
      </WhiteTitledCard>
    </div>
  );
};

export default OrderManagement;
